import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { useAppAccountSwitchOrganisationCreate } from '@shared/api/lib/app/app';
import { OrganizationAccountTypeEnum } from '@shared/api/types';
import sentry from '@shared/services/sentry';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import useAuth from './use-auth';

const useSwitchOrganisation = () => {
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const { mutateAsync, isLoading } = useAppAccountSwitchOrganisationCreate({
    request: useAxiosConfig(),
  });

  const switchOrganisation = async (organisationUuid: string) => {
    try {
      const currentIsParent =
        user?.organizationAccountType === OrganizationAccountTypeEnum.parent;
      const currentLocation = `${location.pathname}${location.search}`;

      await mutateAsync({ data: { organisationUuid } });
      queryClient.clear();
      const refreshed = await refreshUser();

      // The group dashboard (/group/*) and main app share no routes, so we can
      // only preserve the current URL when both orgs render the same route tree —
      // i.e. both are parent orgs, or neither is. Reading the account back after
      // the switch is what tells us which tree we've landed in; without it, fall
      // back to '/' rather than keep a URL that may not exist there.
      const targetIsParent =
        refreshed?.organizationAccountType ===
        OrganizationAccountTypeEnum.parent;
      const destination =
        refreshed && targetIsParent === currentIsParent ? currentLocation : '/';

      navigate(destination);
    } catch (error) {
      sentry.log(error);
      throw error;
    }
  };

  return { switchOrganisation, isLoading };
};

export default useSwitchOrganisation;
